import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'
import { http, logoutSession } from '../api/http'
import {
  readSession,
  startSession,
  subscribeSession,
  updateSessionProfile,
  type SessionPayload,
  type SessionProfile,
  type SessionSnapshot
} from '../auth/session'

interface TokenResponse extends SessionPayload { expiresAtUtc: string }

export const useAuthStore = defineStore('auth', () => {
  const initial = readSession()
  const accessToken = ref(initial.accessToken)
  const profile = ref<SessionProfile | null>(initial.profile)
  const permissions = ref<string[]>(initial.permissions)
  const roles = ref<string[]>(initial.roles)
  const mustChangePassword = ref(initial.mustChangePassword)
  const authenticated = computed(() => Boolean(profile.value && (accessToken.value || localStorage.getItem('qone.access'))))
  let loginAttempt = 0

  function applySession(snapshot: SessionSnapshot) {
    accessToken.value = snapshot.accessToken
    profile.value = snapshot.profile
    roles.value = snapshot.roles
    permissions.value = snapshot.permissions
    mustChangePassword.value = snapshot.mustChangePassword
  }

  const unsubscribe = subscribeSession(applySession)
  onScopeDispose(unsubscribe)

  function can(permission: string) { return permissions.value.includes(permission) }
  function hasRole(role: string) { return roles.value.includes(role) }
  async function login(userNameOrEmail: string, password: string) {
    const attempt = ++loginAttempt
    const { data } = await http.post<TokenResponse>('/auth/login', { userNameOrEmail, password, rememberMe: true })
    if (attempt === loginAttempt) startSession(data)
  }
  /** "Đăng nhập bằng Q One": đổi access token của Q One Platform lấy phiên CRM (tài khoản cùng email). */
  async function loginWithPlatform(platformAccessToken: string) {
    const attempt = ++loginAttempt
    const { data } = await http.post<TokenResponse>('/auth/platform-login', { accessToken: platformAccessToken, rememberMe: true })
    if (attempt === loginAttempt) startSession(data)
  }
  function setProfile(next: SessionProfile) { updateSessionProfile(next) }
  // Đăng xuất báo server thu hồi refresh token (M67); xóa phiên cục bộ dù server không trả lời.
  async function logout() {
    loginAttempt += 1
    try { await logoutSession() } catch { /* Local logout completed; remote revocation is unconfirmed. */ }
  }
  return { profile, roles, permissions, mustChangePassword, authenticated, can, hasRole, login, loginWithPlatform, logout, setProfile }
})
